/**
 * Keeps the price-anchored DOM overlays (order / position / alert lines, the
 * order preview, DCA) inside the price pane while order-flow line panes sit
 * under it.
 *
 * `PricePaneClip` is always mounted (so toggling the panes never remounts the
 * lines and loses a drag or an open menu). Without panes (`bottom` null) it is
 * a full-size, pointer-transparent box: the overlays position and behave
 * exactly as before. With panes it is cut at the price pane's bottom and
 * clips: a line whose price is below the pane is neither drawn nor clickable
 * over the line panes. `PaneEdgeBadges` then shows those prices on the pane
 * edge instead.
 */
import React from 'react';
import type { PriceOverlayItem } from './flowLinePanes';
import type { BrokerOrder } from '../../store/brokerStore';

export const PricePaneClip: React.FC<{ bottom: number | null; children?: React.ReactNode }> = ({ bottom, children }) => (
  <div
    className="price-pane-clip"
    data-clipped={bottom !== null ? 'true' : undefined}
    style={{
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      ...(bottom !== null ? { height: bottom, overflow: 'hidden' } : { bottom: 0 }),
      pointerEvents: 'none',
    }}
  >
    {children}
  </div>
);

/**
 * Where a price line's label (its control pill, centred on `labelY`) goes
 * while the price pane is cut at `bottom` (null = no line panes, nothing to
 * do): wholly inside the pane, so the clip never cuts it in half and it never
 * reaches over a line pane (where a click must never become an order). Null
 * when the line itself (`lineY`) is below the pane: the line is not drawn
 * there, PaneEdgeBadges shows its price instead, so its label goes too.
 */
export function labelYInPane(lineY: number, labelY: number, halfHeight: number, bottom: number | null): number | null {
  if (bottom === null) return labelY;
  if (lineY > bottom) return null;
  const lo = halfHeight;
  const hi = bottom - halfHeight;
  if (hi < lo) return bottom / 2;
  return Math.min(hi, Math.max(lo, labelY));
}

export const PaneEdgeBadges: React.FC<{ top: number | null; items: readonly PriceOverlayItem[] }> = ({ top, items }) => {
  if (top === null || items.length === 0) return null;
  return (
    <div className="pane-edge-badges" data-testid="pane-edge-badges" style={{ top: top - 20 }}>
      {items.map((it) => (
        <span
          key={it.key}
          className="pane-edge-badge"
          data-testid="pane-edge-badge"
          style={{ borderColor: it.color, color: it.color }}
          title={`${it.label} ${it.price} is below the price pane`}
        >
          ▼ {it.label} {it.price}
        </span>
      ))}
    </div>
  );
};

const WORKING = new Set(['Working', 'Accepted', 'Pending']);

export interface PriceOverlaySources {
  orders: readonly Pick<BrokerOrder, 'id' | 'accountId' | 'action' | 'orderQty' | 'ordType' | 'ordStatus' | 'price' | 'stopPrice'>[];
  accountId: number | null;
  position: { netPos: number; netPrice: number } | null;
  orderLines: readonly { id: string; price: number }[];
  alerts: readonly { id: string; price?: number | null; alertType?: string }[];
  draft: { kind: 'TP' | 'SL'; price: number } | null;
}

/** Everything the chart draws at a price as a DOM line, as badge candidates (same filters as the lines). */
export function priceOverlayItems(src: PriceOverlaySources): PriceOverlayItem[] {
  const out: PriceOverlayItem[] = [];
  if (src.position && src.position.netPos !== 0 && src.position.netPrice > 0) {
    out.push({ key: 'position', label: `Pos ${src.position.netPos > 0 ? '+' : ''}${src.position.netPos}`, price: src.position.netPrice, color: '#38bdf8' });
  }
  for (const o of src.orders) {
    if (src.accountId !== null && o.accountId !== src.accountId) continue;
    if (!WORKING.has(o.ordStatus)) continue;
    const price = o.stopPrice || o.price;
    if (!price || price <= 0) continue;
    out.push({
      key: `order:${o.id}`,
      label: `${o.action} ${o.ordType} ${o.orderQty}`,
      price,
      color: o.action === 'Buy' ? '#22c55e' : '#ef4444',
    });
  }
  for (const l of src.orderLines) out.push({ key: `line:${l.id}`, label: 'Order', price: l.price, color: '#fbbf24' });
  for (const a of src.alerts) {
    if (a.price == null || a.alertType === 'volume_spike' || a.alertType === 'volume_above') continue;
    out.push({ key: `alert:${a.id}`, label: 'Alert', price: a.price, color: '#f59e0b' });
  }
  if (src.draft) out.push({ key: 'draft', label: `Draft ${src.draft.kind}`, price: src.draft.price, color: '#a78bfa' });
  return out;
}

/** Same badges (keys and prices) in the same order? */
export function sameOverlayItems(a: readonly PriceOverlayItem[], b: readonly PriceOverlayItem[]): boolean {
  return a.length === b.length && a.every((x, i) => x.key === b[i].key && x.price === b[i].price && x.label === b[i].label);
}
